import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { signOut, useTranslation, useAuthSession } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';

interface RedirectIfAuthedProps {
  children: ReactNode;
}

/**
 * Inverse of <AuthGuard/>: if a session exists, redirect the user away from
 * a public page (/, /auth/signin, /auth/signup) to /dashboard when they
 * have active membership, or to an access state when they do not.
 *
 * Uses render-phase <Navigate replace/> — NO useEffect — to avoid flashing
 * the public form for one commit before the redirect. Matches the
 * anti-flicker pattern F-001 AC11 locked in for SignupRoute.
 *
 * Deliberately reuses useCurrentStore so the store_members query is
 * deduplicated with AuthGuard (single cache entry, single network call on
 * the signin → dashboard transition). See
 * .dukanti/research/bugfix-B-2026-04-14-admin-ux-nav-and-authed-redirect/01-diagnosis.md
 * item 7 for the query-key alignment rationale.
 *
 * Security: the redirect target is hard-coded (`/dashboard`).
 * We do NOT consume a `redirectTo`/`next` query param — see
 * 01-security-assessment.md for the open-redirect mitigation.
 */
export function RedirectIfAuthed({ children }: RedirectIfAuthedProps) {
  const { t } = useTranslation();
  const authSession = useAuthSession(supabase);
  const current = useCurrentStore(authSession);

  // Loading: session hydrating OR store membership query in flight.
  if (authSession.isLoading || (authSession.session && current.isLoading)) {
    return (
      <div
        data-testid="redirect-if-authed-loading"
        className="flex min-h-screen items-center justify-center"
      >
        <p className="text-sand-500">{t('common.loading')}</p>
      </div>
    );
  }

  // Error: session resolved but store_members query failed.
  if (authSession.session && current.isError) {
    return (
      <div
        data-testid="redirect-if-authed-error"
        className="flex min-h-screen items-center justify-center"
      >
        <div className="text-center">
          <p className="text-sand-700">{t('errors.networkFailed')}</p>
          <button type="button" className="mt-4 underline" onClick={() => current.refetch()}>
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  // Render-phase redirect — NO useEffect, no flicker.
  if (authSession.session && !current.isLoading && !current.isError) {
    if (current.storeId) {
      return <Navigate to="/dashboard" replace />;
    }
    if (current.merchantStatus === 'approved') {
      return <Navigate to="/create-store" replace />;
    }
    return <AuthedAccessState state={current.accessState ?? 'pending'} />;
  }

  // No session → passthrough to the public page body.
  return <>{children}</>;
}

function AuthedAccessState({ state }: { state: 'member' | 'pending' | 'unauthorized' }) {
  const { t } = useTranslation();
  const isPending = state === 'pending';
  const handleSignOut = (): void => {
    void signOut(supabase).finally(() => {
      window.location.assign('/auth/signin');
    });
  };

  return (
    <main
      data-testid={isPending ? 'admin-access-pending-state' : 'admin-access-unauthorized-state'}
      className="bg-background flex min-h-screen items-center justify-center px-4"
    >
      <section className="bg-card border-border max-w-md rounded-xl border p-6 text-center shadow-sm">
        <h1 className="font-display text-foreground text-2xl">
          {isPending
            ? t('admin.authBoundary.pendingTitle')
            : t('admin.authBoundary.unauthorizedTitle')}
        </h1>
        <p className="text-muted-foreground mt-3 text-sm leading-6">
          {isPending
            ? t('admin.authBoundary.pendingBody')
            : t('admin.authBoundary.unauthorizedBody')}
        </p>
        <button type="button" className="mt-5 underline" onClick={handleSignOut}>
          {t('admin.authBoundary.signOut')}
        </button>
      </section>
    </main>
  );
}
